import { supabase } from './supabase';
import { monthKey } from './ai-usage';

/**
 * C3 (02/11/2026): the workspace's credits in a month, read by its managers.
 * The grid and the counting live in SQL (`credit_weight_c3`, `credit_ledger`).
 */
export type CreditAction = 'record' | 'chat_message' | 'launch' | 'task_action';

export const CREDIT_ACTION_LABELS: Record<CreditAction, string> = {
  record: 'Fiches traitées',
  chat_message: 'Messages du chat',
  launch: 'Lancements sans liste',
  task_action: 'Actions lancées',
};

export const CREDIT_ACTION_ORDER = Object.keys(CREDIT_ACTION_LABELS) as CreditAction[];

export interface CreditBalance {
  granted: number;
  spent: number;
  seats: number;
  creditsPerSeat: number;
  overageAllowed: boolean;
  overageCap: number | null;
}

export interface PersonCredits {
  userId: string;
  name: string;
  units: Partial<Record<CreditAction, number>>;
  credits: number;
}

export interface MonthCredits {
  balance: CreditBalance;
  people: PersonCredits[];
}

const UNKNOWN_PERSON = 'Membre sans nom affiché';

const isCreditAction = (value: unknown): value is CreditAction =>
  typeof value === 'string' && value in CREDIT_ACTION_LABELS;

/** null when the person is not a manager of the workspace. */
export const loadMonthCredits = async (month: Date): Promise<MonthCredits | null> => {
  const p_month = monthKey(month);
  const [balance, spend] = await Promise.all([
    supabase.rpc('read_credit_balance_c3', { p_month }),
    supabase.rpc('read_credit_spend_c3', { p_month }),
  ]);
  if (balance.error || spend.error) throw new Error('Les crédits ne sont pas accessibles pour le moment.');
  const row = (balance.data as Array<Record<string, unknown>> | null)?.[0];
  if (!row) return null;

  const people = new Map<string, PersonCredits>();
  for (const line of (spend.data as Array<Record<string, unknown>> | null) ?? []) {
    if (!isCreditAction(line.action)) continue;
    const userId = typeof line.user_id === 'string' ? line.user_id : 'unknown';
    const person = people.get(userId) ?? {
      userId,
      name: typeof line.display_name === 'string' && line.display_name ? line.display_name : UNKNOWN_PERSON,
      units: {},
      credits: 0,
    };
    person.units[line.action] = (person.units[line.action] ?? 0) + Number(line.units ?? 0);
    person.credits += Number(line.credits ?? 0);
    people.set(userId, person);
  }

  return {
    balance: {
      granted: Number(row.granted ?? 0),
      spent: Number(row.spent ?? 0),
      seats: Number(row.seats ?? 0),
      creditsPerSeat: Number(row.credits_per_seat ?? 0),
      overageAllowed: row.overage_allowed === true,
      overageCap: row.overage_cap === null || row.overage_cap === undefined ? null : Number(row.overage_cap),
    },
    people: [...people.values()].sort((a, b) => b.credits - a.credits),
  };
};

export const saveCreditOverage = async (allowed: boolean, cap: number | null): Promise<void> => {
  const { error } = await supabase.rpc('set_credit_overage_c3', { p_allowed: allowed, p_cap: allowed ? cap : null });
  if (error) throw new Error('Le réglage n’a pas été enregistré.');
};

export const formatCredits = (credits: number): string => credits.toLocaleString('fr-FR');

/** Share of the month's grant used, 0 when nothing was granted. */
export const usedShare = (balance: Pick<CreditBalance, 'granted' | 'spent'>): number =>
  balance.granted > 0 ? balance.spent / balance.granted : 0;
